import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import { Close as CloseIcon } from 'components/icons';
import { useCallback, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { t } from 'utils/getTranslations';
import { CommonInputProps } from './rhf-inputs/InputTypes';
import { cn } from 'utils/cn';
import InputWrapper, { useFieldLabelsBeside } from './rhf-inputs/InputWrapper';

interface PropsFormItem<T extends FieldValues> extends CommonInputProps<T> {
    onRemove: (val: string) => void;
    addEmail: (val: string) => void;
    watchedValue: string[];
    autoComplete?: string;
}

/** The kit input's box, drawn around the chips and the text line. */
const EMAIL_BOX_CLASS =
    'border-input focus-within:border-ring focus-within:ring-ring/50 relative flex min-h-9 flex-col justify-center rounded-md border py-1.5 pr-10 pl-2.5 focus-within:ring-3';

const EmailListFormItem = <T extends FieldValues>({
    name,
    required = true,
    control,
    watchedValue,
    onRemove,
    addEmail,
    label,
    autoComplete,
    rightLabelComponent,
}: PropsFormItem<T>) => {
    const [value, setValue] = useState('');
    const drafting = useDraftSaveMode();
    const labelBeside = useFieldLabelsBeside();

    const handleOnChange = useCallback((val: string) => {
        setValue(val);
    }, []);

    const maybeAddEmail = () => {
        if (isEmail(value)) {
            addEmail(value);
            handleOnChange('');
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={{ required: { value: !!required && !drafting, message: t('validation.required-field') } }}
            render={({ fieldState: { error } }) => (
                <InputWrapper
                    name={name}
                    label={label}
                    hasErrors={!!error}
                    error={error}
                    gap={0.25}
                    required={required}
                    rightLabelComponent={labelBeside ? undefined : rightLabelComponent}
                    className={labelBeside ? undefined : 'border-divider border-b py-2'}
                >
                    {/* Beside its label the list is a boxed field, its add button inside the box at the end. */}
                    <div className={cn(labelBeside && EMAIL_BOX_CLASS)}>
                        <div className="flex flex-wrap gap-1">
                            {watchedValue?.map((val: string) =>
                                typeof val === 'string' ? (
                                    <div
                                        key={val}
                                        className="bg-divider flex items-center gap-0.5 rounded-sm px-2 text-sm [word-wrap:anywhere]"
                                    >
                                        {val}
                                        <button
                                            type="button"
                                            aria-label={t('remove')}
                                            className="flex size-3 cursor-pointer items-center justify-center text-xs"
                                            onClick={() => onRemove(val as string)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </button>
                                    </div>
                                ) : null
                            )}
                        </div>
                        {/* Underline-less, borderless line of text: the field's own rule is drawn by the
                            wrapper, so a second border here would double it. */}
                        <input
                            type="text"
                            autoComplete={autoComplete}
                            value={value}
                            name="email"
                            required={required}
                            aria-invalid={!!error}
                            className="h-5.5 w-full border-0 bg-transparent text-base/[1.25] outline-none md:text-sm"
                            onChange={(event) => {
                                handleOnChange(event.target.value);
                            }}
                            onBlur={() => maybeAddEmail()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    maybeAddEmail();
                                }
                            }}
                        />
                        {labelBeside && rightLabelComponent ? (
                            <div className="absolute top-1 right-1">{rightLabelComponent}</div>
                        ) : null}
                    </div>
                </InputWrapper>
            )}
        />
    );
};

// Email regex
const EMAIL_PATTERS =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isEmail = (val: string) => String(val).toLowerCase().match(EMAIL_PATTERS);

export default EmailListFormItem;
